import { CoralogixRum } from '@coralogix/browser';

let rumInitialized = false;

function isRumEnabled(): boolean {
  return process.env.REACT_APP_CORALOGIX_RUM_ENABLED === 'true'
    && !!process.env.REACT_APP_CORALOGIX_PUBLIC_KEY;
}

export function initRum(): void {
  if (rumInitialized || !isRumEnabled()) return;

  try {
    CoralogixRum.init({
      public_key: process.env.REACT_APP_CORALOGIX_PUBLIC_KEY || '',
      application: process.env.REACT_APP_NAME || 'siera-ui',
      version: process.env.REACT_APP_VERSION || '0.0.0',
      coralogixDomain: (process.env.REACT_APP_CORALOGIX_DOMAIN || '') as any,
      environment: process.env.REACT_APP_ENV || 'unknown',
      labels: {
        platform: 'web',
        framework: 'react',
      },
      // Any URL that is not on *.leadsquared.com, *.lsq-staging.com, or *.crm4b2c.com will be ignored
      ignoreUrls: [/^(?!https:\/\/[a-zA-Z0-9.-]+\.(leadsquared\.com|lsq-staging\.com|crm4b2c\.com)).*/],
      ignoreErrors: [
        /ResizeObserver loop limit exceeded/i,
        /ResizeObserver loop completed with undelivered notifications/i,
      ],
      sessionConfig: {
        sessionSampleRate: 100,
      },
      traceParentInHeader: {
        enabled: true,
      },
      instrumentations: {
        resources: false,
        long_tasks: false,
      },
    });

    rumInitialized = true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Coralogix RUM initialization failed:', error);
  }
}

export function isRumActive(): boolean {
  return rumInitialized;
}

export function setRumUser(user: {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  orgShortCode?: string;
  regionId?: string;
}): void {
  if (!rumInitialized) return;
  CoralogixRum.setUserContext({
    user_id: user.id,
    user_name: user.name || '',
    user_email: user.email,
    user_metadata: {
      role: user.role || '',
      org: user.orgShortCode || '',
      region: user.regionId || '',
    },
  });
}

export function clearRumUser(): void {
  if (!rumInitialized) return;
  CoralogixRum.setUserContext({
    user_id: '',
    user_name: '',
    user_email: '',
    user_metadata: {},
  });
}

export function reportError(error: Error, context?: Record<string, string>): void {
  if (!rumInitialized) return;
  CoralogixRum.error(error.message, {
    ...context,
    stack: error.stack || '',
  });
}

export function reportApiError(config: { method?: string; url?: string }, response?: { status?: number; data?: unknown }): void {
  if (!rumInitialized) return;
  const endpoint = `${(config.method || 'UNKNOWN').toUpperCase()} ${config.url || 'unknown'}`;
  let responseBody: string;
  try {
    responseBody = typeof response?.data === 'string' ? response.data : JSON.stringify(response?.data ?? '');
  } catch {
    responseBody = '[unserializable]';
  }
  CoralogixRum.error(`API Error: ${endpoint}`, {
    source: 'ApiInterceptor',
    method: (config.method || '').toUpperCase(),
    url: config.url || '',
    status: String(response?.status ?? ''),
    response: responseBody,
  });
}

export function trackCustomEvent(name: string, attributes?: Record<string, string>): void {
  if (!rumInitialized) return;
  CoralogixRum.info(name, attributes);
}
